import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Grid,
  Box,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Formik, Form } from "formik";
import {
  AlertComponent,
  CountryCodeWithPhoneNumber,
  TextInputComponent,
} from "../../../../../../../common/components";
import "../../campaign-view-details-view-activities-info/common-dialog-task/common-dialog-task.scss";
import { useSetLoading } from "../../../../../../../common/hooks/appearance";

import { leadCreate } from "../../../../../../../api/lead";
import { initialValues, validationSchema } from "./add-lead-helper";
import "./add-lead.scss";

interface CommonTaskDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (values: any) => void;
  title?: string;
  initialTask?: any;
}
export interface OwnerOption {
  value: string;
  label: string;
  email: string;
  avatar?: string | null;
}
const AddLead: React.FC<CommonTaskDialogProps> = ({
  open,
  onClose,
  onSave,
  title = "Create Task",
}) => {
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [loading, setLoading] = useState<boolean>(false);
  const handleAlertClose = () => {
    setAlertOpen(false);
  };

  const handleSave = async (values: any) => {
    setLoading(true);
    try {
      const formData = new FormData();
      const leadDetails = {
        firstName: values.firstName,
        lastName: values.lastName,
        primaryEmail: values.email,
        primaryPhoneNumber: values?.primaryPhoneNumber || null,
        designation: values.designation || null,
        leadStatus: "New Lead",
        leadSource: "Manual",
      };
      Object.entries(leadDetails).forEach(([key, value]) => {
        if (value) formData.append(key, value);
      });

      const response = await leadCreate(formData);

      if (response.data.success) {
        const newLeadId = response.data?.data?.id;

        setLoading(false);
        setAlertMessage("Lead created successfully!");
        setAlertType("success");
        setAlertOpen(true);

        onSave(newLeadId);
      } else {
        setAlertMessage("Failed to save lead.");
        setAlertType("error");
        setAlertOpen(true);
        setLoading(false);
      }
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message ||
          "An error occurred while saving the lead."
      );
      setAlertType("error");
      setAlertOpen(true);
      setLoading(false);
    }
  };

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  const isMobile = useMediaQuery("(max-width:768px)");

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        slotProps={{
          paper: {
            sx: {
              width: isMobile ? "90%" : "650px", // 90% width on mobile
              minWidth: isMobile ? "auto" : "640px",
              position: isMobile ? "fixed" : "absolute",
              top: 0,
              margin: 0,
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: 1,
            paddingLeft: 3,
            borderBottom: "1px solid #E1E1E1",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: "bold !important" }}>
            {title}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values) => handleSave(values)}
        >
          {({ values, setFieldValue, setFieldTouched, touched, errors }) => (
            <Form>
              <div className="task-dialog-container">
                <DialogContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Box display="flex" alignItems="center" gap={11}>
                        <Typography width={100}>First Name</Typography>
                        <Box flex={1}>
                          <TextInputComponent
                            type="text"
                            fieldName="firstName"
                            placeholder="Enter First Name"
                            optional={true}
                            disabled={false}
                            value={values.firstName}
                            onChange={(e: any) =>
                              setFieldValue("firstName", e?.target?.value)
                            }
                            isRequired={true}
                            isName={true}
                            minlength="3"
                            maxlength="50"
                          />
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={11}
                        className="campaign-input-component"
                      >
                        <Typography width={100}>Last Name</Typography>
                        <Box flex={1}>
                          <TextInputComponent
                            type="text"
                            fieldName="lastName"
                            placeholder="Enter Last Name"
                            optional={true}
                            disabled={false}
                            value={values.lastName}
                            onChange={(e: any) =>
                              setFieldValue("lastName", e?.target?.value)
                            }
                            isRequired={true}
                            isName={true}
                            minlength="3"
                            maxlength="50"
                          />
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Box display="flex" alignItems="center" gap={11}>
                        <Typography width={100}>Email</Typography>
                        <Box flex={1}>
                          <TextInputComponent
                            type="email"
                            fieldName="email"
                            placeholder="Enter Email"
                            optional={true}
                            disabled={false}
                            value={values.email}
                            onChange={(e: any) =>
                              setFieldValue("email", e?.target?.value)
                            }
                            isRequired={true}
                          />
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box display="flex" alignItems="center" gap={11}>
                        <Typography width={100} sx={{ whiteSpace: "nowrap" }}>
                          Primary Phone Number
                        </Typography>
                        <Box flex={1}>
                          <div className="form-group">
                            <CountryCodeWithPhoneNumber
                              fieldName="primaryPhoneNumber"
                              placeholder="Enter Primary Phone Number"
                              onChange={(
                                value: string,
                                country: any,
                                one: any,
                                two: any
                              ) => {
                                const countryCode = country?.dialCode;

                                if (!countryCode) {
                                  console.warn(
                                    "Missing dialCode in country object"
                                  );
                                  return;
                                }

                                setFieldValue("primaryPhoneNumber", two);
                              }}
                              onBlur={() => {
                                setFieldTouched("primaryPhoneNumber", true);
                              }}
                              value={values?.primaryPhoneNumber || "+1"}
                              defaultCountry="us"
                              optional={true}
                              // countryCodeEditable
                            />
                            {touched.primaryPhoneNumber &&
                              touched?.primaryPhoneNumber && (
                                <div className="error-message">
                                  {errors.primaryPhoneNumber}
                                </div>
                              )}
                          </div>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box display="flex" alignItems="center" gap={11}>
                        <Typography width={100}>Designation</Typography>
                        <Box flex={1}>
                          <TextInputComponent
                            type="text"
                            fieldName="designation"
                            placeholder="Enter Designation"
                            optional={false}
                            disabled={false}
                            value={values.designation}
                            onChange={(e: any) =>
                              setFieldValue("designation", e?.target?.value)
                            }
                            maxlength="100"
                          />
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </DialogContent>

                <DialogActions sx={{ margin: "12px" }}>
                  <button onClick={onClose} className="cancel-button">
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="cancel-button next-button-wrapper"
                  >
                    Save
                  </button>
                </DialogActions>
              </div>
            </Form>
          )}
        </Formik>
      </Dialog>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        open={alertOpen}
        onClose={handleAlertClose}
      />
    </>
  );
};

export default AddLead;
